import { supabase } from '../lib/supabase';

/**
 * User Account Management Service
 * Comprehensive user management tools for admin staff
 */

export const userAccountService = {
  /**
   * Search users with advanced filters
   */
  async searchUsers(filters = {}) {
    try {
      let query = supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (filters?.search) {
        query = query?.or(
          `full_name.ilike.%${filters?.search}%,email.ilike.%${filters?.search}%`
        );
      }

      if (filters?.role && filters?.role !== 'all') {
        query = query?.eq('role', filters?.role);
      }

      if (filters?.accountStatus) {
        if (filters?.accountStatus === 'locked') {
          query = query?.eq('account_locked', true);
        } else if (filters?.accountStatus === 'active') {
          query = query?.eq('account_locked', false);
        }
      }

      if (filters?.emailVerified !== undefined && filters?.emailVerified !== 'all') {
        query = query?.eq('email_verified', filters?.emailVerified === 'verified');
      }

      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(user => ({
        id: user?.id,
        email: user?.email,
        fullName: user?.full_name,
        role: user?.role,
        avatarUrl: user?.avatar_url,
        phoneNumber: user?.phone_number,
        location: user?.location,
        emailVerified: user?.email_verified,
        accountLocked: user?.account_locked,
        accountLockedAt: user?.account_locked_at,
        lockoutReason: user?.lockout_reason,
        accountStatus: user?.account_status,
        profileVisibility: user?.profile_visibility,
        searchable: user?.searchable,
        lastActiveAt: user?.last_active_at,
        createdAt: user?.created_at,
        updatedAt: user?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  },

  /**
   * Get comprehensive user details
   */
  async getUserDetails(userId) {
    try {
      const { data, error } = await supabase?.rpc('admin_get_user_details', {
        target_user_id: userId
      });

      if (error) throw error;

      return {
        user: {
          id: data?.user?.id,
          email: data?.user?.email,
          fullName: data?.user?.full_name,
          role: data?.user?.role,
          avatarUrl: data?.user?.avatar_url,
          phoneNumber: data?.user?.phone_number,
          location: data?.user?.location,
          accountLocked: data?.user?.account_locked,
          accountLockedAt: data?.user?.account_locked_at,
          lockoutReason: data?.user?.lockout_reason,
          emailVerified: data?.user?.email_verified,
          accountStatus: data?.user?.account_status,
          profileVisibility: data?.user?.profile_visibility,
          searchable: data?.user?.searchable,
          lastActiveAt: data?.user?.last_active_at,
          createdAt: data?.user?.created_at,
          updatedAt: data?.user?.updated_at
        },
        activity: data?.activity,
        recentTickets: data?.recent_tickets || [],
        recentApplications: data?.recent_applications || []
      };
    } catch (error) {
      console.error('Error getting user details:', error);
      throw error;
    }
  },

  /**
   * Get user activity timeline
   */
  async getUserActivityTimeline(userId, limit = 50) {
    try {
      const { data, error } = await supabase?.rpc('admin_get_user_activity_timeline', {
        target_user_id: userId,
        limit_count: limit
      });

      if (error) throw error;

      return data?.map(event => ({
        eventTime: event?.event_time,
        eventType: event?.event_type,
        eventDescription: event?.event_description,
        eventMetadata: event?.event_metadata
      })) || [];
    } catch (error) {
      console.error('Error getting user activity timeline:', error);
      throw error;
    }
  },

  /**
   * Admin password reset for user
   */
  async adminResetPassword(userId, reason) {
    try {
      const { data, error } = await supabase?.rpc('admin_reset_user_password', {
        target_user_id: userId,
        admin_reason: reason
      });

      if (error) throw error;

      // Also send password reset email
      const { data: userData } = await supabase
        ?.from('user_profiles')
        ?.select('email')
        ?.eq('id', userId)
        ?.single();

      if (userData?.email) {
        await supabase?.auth?.resetPasswordForEmail(userData?.email, {
          redirectTo: `${window.location?.origin}/reset-password`
        });
      }

      return { success: true, message: 'Password reset initiated successfully' };
    } catch (error) {
      console.error('Error resetting user password:', error);
      throw error;
    }
  },

  /**
   * Unlock user account
   */
  async unlockAccount(userId, reason) {
    try {
      const { error } = await supabase?.rpc('unlock_account', {
        target_user_id: userId,
        admin_note: reason
      });

      if (error) throw error;

      return { success: true, message: 'Account unlocked successfully' };
    } catch (error) {
      console.error('Error unlocking account:', error);
      throw error;
    }
  },

  /**
   * Lock/suspend user account
   */
  async lockAccount(userId, reason) {
    try {
      const { error } = await supabase
        ?.from('user_profiles')
        ?.update({
          account_locked: true,
          account_locked_at: new Date()?.toISOString(),
          lockout_reason: reason,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', userId);

      if (error) throw error;

      // Log admin action
      await supabase?.rpc('log_admin_activity', {
        p_action_type: 'user_management',
        p_action_description: `Account locked: ${reason}`,
        p_target_user_id: userId,
        p_metadata: { action: 'account_lock', reason }
      });

      return { success: true, message: 'Account locked successfully' };
    } catch (error) {
      console.error('Error locking account:', error);
      throw error;
    }
  },

  /**
   * Verify user email (admin override)
   */
  async verifyUserEmail(userId, reason) {
    try {
      const { error } = await supabase
        ?.from('user_profiles')
        ?.update({
          email_verified: true,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', userId);

      if (error) throw error;

      // Log admin action
      await supabase?.rpc('log_admin_activity', {
        p_action_type: 'email_verification_override',
        p_action_description: `Email verified by admin: ${reason}`,
        p_target_user_id: userId,
        p_metadata: { action: 'email_verification', reason }
      });

      return { success: true, message: 'Email verified successfully' };
    } catch (error) {
      console.error('Error verifying email:', error);
      throw error;
    }
  },

  /**
   * Modify user account data
   */
  async modifyUserData(userId, fieldName, newValue, reason) {
    try {
      const { data, error } = await supabase?.rpc('admin_modify_user_data', {
        target_user_id: userId,
        field_name: fieldName,
        new_value: newValue,
        admin_reason: reason
      });

      if (error) throw error;

      return { success: true, message: 'User data modified successfully' };
    } catch (error) {
      console.error('Error modifying user data:', error);
      throw error;
    }
  },

  /**
   * Override account deletion
   */
  async overrideAccountDeletion(userId, reason) {
    try {
      const { data, error } = await supabase?.rpc('admin_override_account_deletion', {
        target_user_id: userId,
        admin_reason: reason
      });

      if (error) throw error;

      return { success: true, message: 'Account deletion prevented successfully' };
    } catch (error) {
      console.error('Error overriding account deletion:', error);
      throw error;
    }
  },

  /**
   * Get user's support tickets
   */
  async getUserTickets(userId) {
    try {
      const { data, error } = await supabase
        ?.from('support_tickets')
        ?.select(`
          *,
          assigned_user:user_profiles!support_tickets_assigned_to_fkey (
            id,
            full_name,
            email
          )
        `)
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(ticket => ({
        id: ticket?.id,
        subject: ticket?.subject,
        description: ticket?.description,
        category: ticket?.category,
        priority: ticket?.priority,
        status: ticket?.status,
        assignedTo: ticket?.assigned_to,
        assignedUser: ticket?.assigned_user ? {
          id: ticket?.assigned_user?.id,
          fullName: ticket?.assigned_user?.full_name,
          email: ticket?.assigned_user?.email
        } : null,
        createdAt: ticket?.created_at,
        updatedAt: ticket?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error getting user tickets:', error);
      throw error;
    }
  },

  /**
   * Get admin activity log for user
   */
  async getUserAdminActivityLog(userId, limit = 50) {
    try {
      const { data, error } = await supabase
        ?.from('admin_activity_log')
        ?.select(`
          *,
          admin:admin_employees!admin_activity_log_admin_id_fkey (
            id,
            employee_id,
            admin_role
          )
        `)
        ?.eq('target_user_id', userId)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;

      return data?.map(log => ({
        id: log?.id,
        actionType: log?.action_type,
        actionDescription: log?.action_description,
        admin: log?.admin ? {
          id: log?.admin?.id,
          employeeId: log?.admin?.employee_id,
          role: log?.admin?.admin_role
        } : null,
        metadata: log?.metadata,
        createdAt: log?.created_at
      })) || [];
    } catch (error) {
      console.error('Error getting admin activity log:', error);
      throw error;
    }
  },

  /**
   * Get user statistics summary
   */
  async getUserStatistics() {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('role, account_locked, email_verified, account_status');

      if (error) throw error;

      const stats = {
        totalUsers: data?.length || 0,
        activeUsers: data?.filter(u => !u?.account_locked)?.length || 0,
        lockedUsers: data?.filter(u => u?.account_locked)?.length || 0,
        verifiedEmails: data?.filter(u => u?.email_verified)?.length || 0,
        unverifiedEmails: data?.filter(u => !u?.email_verified)?.length || 0,
        byRole: {
          jobSeeker: data?.filter(u => u?.role === 'job_seeker')?.length || 0,
          hiringManager: data?.filter(u => u?.role === 'hiring_manager')?.length || 0,
          companyAdmin: data?.filter(u => u?.role === 'company_admin')?.length || 0,
          admin: data?.filter(u => u?.role === 'admin')?.length || 0
        },
        byAccountStatus: {
          active: data?.filter(u => u?.account_status === 'active')?.length || 0,
          deactivated: data?.filter(u => u?.account_status === 'deactivated')?.length || 0,
          deletionScheduled: data?.filter(u => u?.account_status === 'deletion_scheduled')?.length || 0
        }
      };

      return stats;
    } catch (error) {
      console.error('Error getting user statistics:', error);
      throw error;
    }
  }
};

export default userAccountService;